import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  checkVerifier,
  decryptBytes,
  defaultKdfParams,
  deriveKey,
  encryptBytes,
  initCrypto,
  makeVerifier,
  newSalt,
  type Sodium,
} from './crypto';
import { useEncryptionSettings, useUpdateEncryptionSettings } from '../data/queries';
import type { EncryptionMode } from '../data/dto';
import { EncryptionSheet } from './EncryptionSheet';
import { useT } from '../i18n';

/**
 * status:
 *   - loading : sodium / settings still resolving
 *   - unset   : user hasn't chosen yet → prompt on first upload
 *   - none    : chose no encryption
 *   - locked  : e2ee, but no key in memory (need the password on this device)
 *   - ready   : usable (either 'none', or 'e2ee' with the key derived)
 */
export type EncryptionStatus = 'loading' | 'unset' | 'none' | 'locked' | 'ready';

interface EncryptionContextValue {
  status: EncryptionStatus;
  mode: EncryptionMode;
  /** The device-stored password (for the Settings reveal). Null when none/locked. */
  password: string | null;
  hint: string | null;
  busy: boolean;
  error: string | null;
  isEncryptedMode: boolean;
  /** Persist "no encryption". */
  chooseNone: () => Promise<void>;
  /** Set up E2EE: derive key, store salt+verifier server-side, save password on device. */
  chooseEncrypted: (password: string, hint?: string) => Promise<void>;
  /** Unlock on a device that has the settings but not the password. */
  unlock: (password: string) => Promise<boolean>;
  /**
   * Ensure we're in a state where uploads can proceed. Resolves true when ready
   * (or unencrypted), false if the user cancelled. Opens the setup/unlock sheet
   * as needed.
   */
  ensureReady: () => Promise<boolean>;
  /** Encrypt outgoing bytes when in e2ee mode; pass-through otherwise. */
  encryptForUpload: (bytes: Uint8Array) => { bytes: Uint8Array; encrypted: boolean };
  /** Decrypt downloaded e2ee bytes (throws if locked). */
  decrypt: (bytes: Uint8Array) => Uint8Array;
}

const Ctx = createContext<EncryptionContextValue | null>(null);

const PW_KEY = 'babysteps.e2ee.pw';
// NOTE: localStorage is the web equivalent of the device keystore. The native
// iOS/Android apps will store this in Keychain / Keystore instead.
const getPwKey = (babyId: string) => `babysteps.e2ee.pw.${babyId}`;
const loadPw = (babyId: string): string | null => {
  try { return localStorage.getItem(getPwKey(babyId)); } catch { return null; }
};
const savePw = (babyId: string, pw: string): void => {
  try { localStorage.setItem(getPwKey(babyId), pw); } catch { /* ignore */ }
};
const clearPw = (babyId: string): void => {
  try { localStorage.removeItem(getPwKey(babyId)); } catch { /* ignore */ }
};

export function EncryptionProvider({ children, activeBabyId }: { children: ReactNode; activeBabyId: string }) {
  const t = useT();
  const settingsQ = useEncryptionSettings(activeBabyId);
  const updateSettings = useUpdateEncryptionSettings(activeBabyId);

  const sodiumRef = useRef<Sodium | null>(null);
  const keyRef = useRef<Uint8Array | null>(null);
  const [sodiumReady, setSodiumReady] = useState(false);

  const [status, setStatus] = useState<EncryptionStatus>('loading');
  const [password, setPassword] = useState<string | null>(() => loadPw(activeBabyId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settings = settingsQ.data;
  const mode: EncryptionMode = settings?.mode ?? 'unset';
  const hint = settings?.password_hint ?? null;

  useEffect(() => {
    let alive = true;
    initCrypto().then((s) => {
      sodiumRef.current = s;
      if (alive) setSodiumReady(true);
    });
    return () => { alive = false; };
  }, []);

  // Invalidate key and re-read stored password when active baby changes
  useEffect(() => {
    keyRef.current = null;
    setPassword(loadPw(activeBabyId));
    setStatus('loading');
  }, [activeBabyId]);

  // Derive (and verify) the key whenever inputs change.
  useEffect(() => {
    if (!sodiumReady || settingsQ.isLoading || !settings) {
      setStatus('loading');
      return;
    }
    if (settings.mode === 'unset') { keyRef.current = null; setStatus('unset'); return; }
    if (settings.mode === 'none')  { keyRef.current = null; setStatus('none');  return; }

    // e2ee
    if (keyRef.current) { setStatus('ready'); return; }
    if (!password) { setStatus('locked'); return; }
    if (!settings.kdf_salt_b64 || !settings.verifier_nonce_b64 || !settings.verifier_cipher_b64) {
      setStatus('locked');
      return;
    }

    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const s = sodiumRef.current!;
        const key = deriveKey(s, password, {
          salt_b64: settings.kdf_salt_b64!,
          ops: settings.kdf_ops ?? s.crypto_pwhash_OPSLIMIT_INTERACTIVE,
          mem: settings.kdf_mem ?? s.crypto_pwhash_MEMLIMIT_INTERACTIVE,
          algo: settings.kdf_algo ?? 'argon2id',
        });
        const ok = checkVerifier(s, key, {
          nonce_b64: settings.verifier_nonce_b64!,
          cipher_b64: settings.verifier_cipher_b64!,
        });
        if (cancelled) return;
        if (ok) {
          keyRef.current = key;
          setStatus('ready');
        } else {
          // Stored password no longer matches (e.g. user reset it elsewhere).
          clearPw(activeBabyId);
          setPassword(null);
          keyRef.current = null;
          setStatus('locked');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sodiumReady, settingsQ.isLoading, settings, password]);

  const chooseNone = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await updateSettings.mutateAsync({ mode: 'none' });
      keyRef.current = null;
      clearPw(activeBabyId);
      setPassword(null);
      setStatus('none');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('encryption.error.saveChoice', 'Could not save your choice.'));
      throw e;
    } finally {
      setBusy(false);
    }
  }, [updateSettings, activeBabyId]);

  const chooseEncrypted = useCallback(async (pw: string, hintText?: string) => {
    const s = sodiumRef.current;
    if (!s) throw new Error('Encryption is still initialising.');
    setBusy(true);
    setError(null);
    try {
      const salt = newSalt(s);
      const params = defaultKdfParams(s, salt);
      const key = deriveKey(s, pw, params);
      const verifier = makeVerifier(s, key);
      await updateSettings.mutateAsync({
        mode: 'e2ee',
        kdf_algo: params.algo,
        kdf_salt_b64: params.salt_b64,
        kdf_ops: params.ops,
        kdf_mem: params.mem,
        verifier_nonce_b64: verifier.nonce_b64,
        verifier_cipher_b64: verifier.cipher_b64,
        password_hint: hintText && hintText !== '' ? hintText : undefined,
      });
      keyRef.current = key;
      savePw(activeBabyId, pw);
      setPassword(pw);
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('encryption.error.enable', 'Could not enable encryption.'));
      throw e;
    } finally {
      setBusy(false);
    }
  }, [updateSettings, activeBabyId]);

  const unlock = useCallback(async (pw: string): Promise<boolean> => {
    const s = sodiumRef.current;
    if (!s || !settings || settings.mode !== 'e2ee'
        || !settings.kdf_salt_b64 || !settings.verifier_nonce_b64 || !settings.verifier_cipher_b64) {
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const key = deriveKey(s, pw, {
        salt_b64: settings.kdf_salt_b64,
        ops: settings.kdf_ops ?? s.crypto_pwhash_OPSLIMIT_INTERACTIVE,
        mem: settings.kdf_mem ?? s.crypto_pwhash_MEMLIMIT_INTERACTIVE,
        algo: settings.kdf_algo ?? 'argon2id',
      });
      const ok = checkVerifier(s, key, {
        nonce_b64: settings.verifier_nonce_b64,
        cipher_b64: settings.verifier_cipher_b64,
      });
      if (ok) {
        keyRef.current = key;
        savePw(activeBabyId, pw);
        setPassword(pw);
        setStatus('ready');
        return true;
      }
      setError(t('encryption.error.wrongPassword', "That password doesn't match. Please try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }, [settings, activeBabyId]);

  const encryptForUpload = useCallback((bytes: Uint8Array): { bytes: Uint8Array; encrypted: boolean } => {
    const s = sodiumRef.current;
    if (settings?.mode === 'e2ee' && keyRef.current && s) {
      return { bytes: encryptBytes(s, keyRef.current, bytes), encrypted: true };
    }
    return { bytes, encrypted: false };
  }, [settings?.mode]);

  const decrypt = useCallback((bytes: Uint8Array): Uint8Array => {
    const s = sodiumRef.current;
    if (!s || !keyRef.current) throw new Error('Photos are locked — enter your password to view them.');
    return decryptBytes(s, keyRef.current, bytes);
  }, []);

  // ── Sheet orchestration (setup / unlock) ──────────────────────────────
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetIntent, setSheetIntent] = useState<'setup' | 'unlock'>('setup');
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const ensureReady = useCallback((): Promise<boolean> => {
    if (status === 'ready' || status === 'none') return Promise.resolve(true);
    if (status === 'loading') return Promise.resolve(false);
    setSheetIntent(status === 'unset' ? 'setup' : 'unlock');
    setSheetOpen(true);
    return new Promise<boolean>((resolve) => { resolveRef.current = resolve; });
  }, [status]);

  const finishSheet = useCallback((ok: boolean) => {
    setSheetOpen(false);
    setError(null);
    resolveRef.current?.(ok);
    resolveRef.current = null;
  }, []);

  const value = useMemo<EncryptionContextValue>(
    () => ({
      status,
      mode,
      password,
      hint,
      busy,
      error,
      isEncryptedMode: mode === 'e2ee',
      chooseNone,
      chooseEncrypted,
      unlock,
      ensureReady,
      encryptForUpload,
      decrypt,
    }),
    [
      status,
      mode,
      password,
      hint,
      busy,
      error,
      chooseNone,
      chooseEncrypted,
      unlock,
      ensureReady,
      encryptForUpload,
      decrypt,
    ],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <EncryptionSheet
        open={sheetOpen}
        intent={sheetIntent}
        onResolved={finishSheet}
      />
    </Ctx.Provider>
  );
}

export function useEncryption(): EncryptionContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useEncryption must be used within EncryptionProvider');
  return ctx;
}
